/**
 * YAC-2026-009 PR7b。運営のサポートのセッションの見分け（ほかに依存しない小さな関数だけ）。
 * axiosInstance・PrivateRoute・components/Support から使う。
 */

/**
 * サポートのセッションが終わった（サーバーの 401 support_access_ended。手で終わらせた・期限が切れた・運営のログインの
 * 締め切りを過ぎたなど）。API の要求でも、Cookie の更新でも同じ code が返る。
 */
export const SUPPORT_ENDED_CODE = 'support_access_ended';

export function isSupportAccessEnded(error: unknown): boolean {
  const response = (error as { response?: { status?: number; data?: { code?: unknown } } })?.response;
  return response?.status === 401 && response?.data?.code === SUPPORT_ENDED_CODE;
}

/**
 * このタブで「戻る」を始めたか。戻るはサポートのセッションを失効させるので、その前に送った要求（ダッシュボードの
 * 読み込みなど）が後から 401 support_access_ended で返る。そのときに終わりの画面へ移すと、戻った先の運営の画面への
 * 移動を上書きしてしまう。戻っている間と戻った後は、終わりの画面へ移さない（要求の失敗としてだけ返す）。
 */
let leaving = false;

export function markLeavingSupport(value: boolean): void {
  leaving = value;
}

export function isLeavingSupport(): boolean {
  return leaving;
}

export interface SupportClaims {
  /** SupportAccess の id。 */
  accessId: number;
  /** 入った運営の id。 */
  operatorId: number;
  /** 運営のログインの締め切り（ミリ秒）。無ければ null。 */
  otpDeadline: number | null;
  /**
   * サポートとして振る舞える期限（claim support_until、ミリ秒）。サーバーが min(サポートの期限, 運営の締め切り) を
   * 入れる（#393）。この後の Cookie は「戻る」にだけ使える。無ければ null（古いトークン）。
   */
  supportUntil: number | null;
}

const isId = (value: unknown): value is number => typeof value === 'number' && Number.isInteger(value);

/** access（JWT。署名は見ない）がサポートのセッションのものなら、その claim。違う・読めなければ null。 */
export function supportClaimsOf(access: string | null | undefined): SupportClaims | null {
  if (!access) return null;
  const parts = access.split('.');
  if (parts.length !== 3) return null;
  let payload: Record<string, unknown>;
  try {
    payload = JSON.parse(
      decodeURIComponent(
        atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
          .split('')
          .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
          .join('')
      )
    );
  } catch {
    return null;
  }
  if (!payload || typeof payload !== 'object') return null;
  if (!isId(payload.support_access) || !isId(payload.support_operator)) return null;
  const deadline = payload.otp_deadline;
  const until = payload.support_until;
  return {
    accessId: payload.support_access,
    operatorId: payload.support_operator,
    otpDeadline: typeof deadline === 'number' ? deadline * 1000 : null,
    supportUntil: typeof until === 'number' ? until * 1000 : null,
  };
}
